import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useEffect, useState } from "react";
import Axios from "axios";
import { Table } from "react-bootstrap";
import { format, parseISO } from "date-fns";
import "./managereturn.css"; 

function Managereturn() {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editId, setEditId] = useState(null);
  const [editedQuantities, setEditedQuantities] = useState({});
  const [newEntries, setNewEntries] = useState([]);
  const [removedEntryIds, setRemovedEntryIds] = useState([]);
  const [listData, setListData] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
    const list = Axios.get(`${INVENTORY_API_BASE_URL}/getlist`);
    list
      .then((response) => {
        setListData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching list data:", error);
      });
  }, []);

  const handleAddRow = (currentRow) => {
    const discount = currentRow.Entries[0]?.discount || 0;

    const newId = Date.now().toString();
    const newEntry = {
      _id: newId,
      SKU: "",
      Title: "",
      Edition: "",
      Medium: "",
      mrp: 0,
      discount,
      Return_Quantity: 0,
      Good_Quantity: 0,
    };

    setNewEntries((prev) => [...prev, newEntry]);
    setEditedQuantities((prev) => ({
      ...prev,
      [newId]: 0,
    }));
  };

  const fetchData = async () => {
    const response = await Axios.get(
      `${INVENTORY_API_BASE_URL}/getManagereturn`
    );
    const sortedData = response.data.sort(
      (a, b) => new Date(b.Date) - new Date(a.Date)
    );
    setData(sortedData);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleEdit = (row) => {
    setEditId(row._id);
    const initialQuantities = {};
    row.Entries.forEach((entry) => {
      initialQuantities[entry._id] = entry.Return_Quantity;
    });
    setEditedQuantities(initialQuantities);
    setNewEntries([]);
    setRemovedEntryIds([]);
  };

  const handleSave = async (row) => {
    const discount = row.Entries[0]?.discount || 0; 

    const payload = {
      updatedQuantities: Object.entries(editedQuantities).map(
        ([id, Return_Quantity]) => ({
          _id: id,
          Return_Quantity: Number(Return_Quantity) || 0,
        })
      ),
      newEntries: newEntries.map((entry) => {
        const returnQty = Number(editedQuantities[entry._id]) || 0;
        return {
          ...entry,
          Return_Quantity: returnQty,
          Good_Quantity: returnQty,
          discount, 
        };
      }),
      removedEntryIds,
    };

    try {
      await Axios.put(
        `${INVENTORY_API_BASE_URL}/api/returngrn/${row._id}`,
        payload
      );
      await fetchData();
      setEditId(null);
      setEditedQuantities({});
      setNewEntries([]);
      setRemovedEntryIds([]);
    } catch (error) {
      console.error("Failed to update quantities:", error);
    }
  };

  const handleCancel = () => {
    setEditId(null);
    setEditedQuantities({});
    setNewEntries([]);
    setRemovedEntryIds([]);
  };

  const updateNewEntry = (index, field, value) => {
    const updated = [...newEntries];
    updated[index][field] = value;
    setNewEntries(updated);
  };

  return (
    <main className="managereturn-page">
      <div className="managereturn-shell">
        <header className="managereturn-hero">
          <div>
            <p className="managereturn-eyebrow">
              <span aria-hidden="true" />
              INVENTORY / RETURN OPERATIONS
            </p>
            <h1>Manage Return GRNs</h1>
            <p>Review returned goods, adjust quantities, and maintain return records.</p>
          </div>
          <div className="managereturn-document-badge">
            <span>RETURN REGISTER</span>
            <strong>Goods received notes</strong>
            <small><i aria-hidden="true" /> {data.length.toLocaleString()} records</small>
          </div>
        </header>

        <section className="managereturn-summary" aria-label="Return record summary">
          <article className="managereturn-summary-card">
            <span className="managereturn-summary-icon managereturn-summary-icon--blue" aria-hidden="true">GRN</span>
            <div>
              <span className="managereturn-summary-label">Return records</span>
              <strong>{data.length.toLocaleString()}</strong>
              <small>All available return GRNs</small>
            </div>
          </article>
          <article className="managereturn-summary-card">
            <span className="managereturn-summary-icon managereturn-summary-icon--green" aria-hidden="true">PAGE</span>
            <div>
              <span className="managereturn-summary-label">On this page</span>
              <strong>{currentItems.length.toLocaleString()}</strong>
              <small>Showing up to {itemsPerPage} records</small>
            </div>
          </article>
        </section>

        <section className="managereturn-card" aria-labelledby="managereturn-records-title">
          <div className="managereturn-table-toolbar">
            <div>
              <h2 id="managereturn-records-title">Return GRN records</h2>
              <p>View invoice, distributor, SKU, quantity, and bilty details.</p>
            </div>
            <nav className="managereturn-pagination" aria-label="Return record pages">
              <button
                type="button"
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || totalPages === 0}
                className="managereturn-page-button"
                aria-label="Go to previous page"
              >
                <span aria-hidden="true">←</span> Previous
              </button>
              <span className="managereturn-page-indicator">
                Page <strong>{totalPages === 0 ? 0 : currentPage}</strong> of <strong>{totalPages}</strong>
              </span>
              <button
                type="button"
                onClick={goToNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                className="managereturn-page-button"
                aria-label="Go to next page"
              >
                Next <span aria-hidden="true">→</span>
              </button>
            </nav>
          </div>

          <div className="managereturn-range">
            <span className="managereturn-range-dot" aria-hidden="true" />
            {data.length === 0 ? (
              <span>No return records to display</span>
            ) : (
              <span>
                Showing <strong>{indexOfFirstItem + 1}–{Math.min(indexOfLastItem, data.length)}</strong> of{" "}
                <strong>{data.length.toLocaleString()}</strong> return records
              </span>
            )}
          </div>

          {currentItems.length > 0 ? (
            <div className="managereturn-table-wrapper">
              <Table className="managereturn-table" responsive={false}>
                <thead>
                  <tr>
                    <th scope="col">GRN No.</th>
                    <th scope="col">Distributor</th>
                    <th scope="col">SKU</th>
                    <th scope="col">Title</th>
                    <th scope="col">Edition</th>
                    <th scope="col">Medium</th>
                    <th scope="col">Return quantity</th>
                    <th scope="col">Good</th>
                    <th scope="col">Bad</th>
                    <th scope="col">Invoice number</th>
                    <th scope="col">Total quantity</th>
                    <th scope="col">Amount value</th>
                    <th scope="col">Bilty</th>
                    <th scope="col">Date</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((row) => (
                    <React.Fragment key={row._id}>
                      <tr className="managereturn-main-row">
                        <td><span className="managereturn-grn-number">{row.OrderSeq}</span></td>
                        <td className="managereturn-distributor-cell">{row.Distributor || "—"}</td>
                        <td><span className="managereturn-sku">{row.Entries[0]?.SKU || "—"}</span></td>
                        <td className="managereturn-title-cell" title={row.Entries[0]?.Title || ""}>
                          {row.Entries[0]?.Title || "—"}
                        </td>
                        <td>{row.Entries[0]?.Edition || "—"}</td>
                        <td>{row.Entries[0]?.Medium || "—"}</td>
                        <td>
                          {editId === row._id ? (
                            <input
                              className="managereturn-quantity-input"
                              type="number"
                              value={editedQuantities[row.Entries[0]._id] || 0}
                              onChange={(e) =>
                                setEditedQuantities((prev) => ({
                                  ...prev,
                                  [row.Entries[0]._id]: e.target.value,
                                }))
                              }
                              aria-label={"Return quantity for GRN " + row.OrderSeq}
                            />
                          ) : (
                            row.Entries[0]?.Return_Quantity ?? "—"
                          )}
                        </td>
                        <td>{row.Entries[0]?.Good_Quantity ?? "—"}</td>
                        <td>{row.Entries[0]?.Bad_Quantity ?? "—"}</td>
                        <td>{row.Invoice_Number || "—"}</td>
                        <td>{row.totalquantity ?? "—"}</td>
                        <td className="managereturn-amount-cell">
                          ₹ {Number(row.totalAmountValue || 0).toFixed(2)}
                        </td>
                        <td className="managereturn-bilty-cell">
                          {row.Bilty ? (
                            Array.isArray(row.Bilty) ? (
                              row.Bilty.map((file, idx) => (
                                <a
                                  className="managereturn-file-link"
                                  key={idx}
                                  href={"https://d1bspr7swkjn95.cloudfront.net/" + file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                >
                                  <span aria-hidden="true">↧</span> File {idx + 1}
                                </a>
                              ))
                            ) : (
                              <a
                                className="managereturn-file-link"
                                href={"https://d1bspr7swkjn95.cloudfront.net/" + row.Bilty}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                              >
                                <span aria-hidden="true">↧</span> Download
                              </a>
                            )
                          ) : (
                            <span className="managereturn-no-file">No file</span>
                          )}
                        </td>
                        <td>{format(parseISO(row.Date), "dd/MM/yyyy")}</td>
                        <td className="managereturn-actions-cell">
                          {editId === row._id ? (
                            <div className="managereturn-row-actions">
                              <button className="managereturn-action-button managereturn-action-button--save" type="button" onClick={() => handleSave(row)}>Save</button>
                              <button className="managereturn-action-button" type="button" onClick={handleCancel}>Cancel</button>
                              <button className="managereturn-action-button managereturn-action-button--add" type="button" onClick={() => handleAddRow(row)}>+ Add row</button>
                            </div>
                          ) : (
                            <button className="managereturn-action-button managereturn-action-button--edit" type="button" onClick={() => handleEdit(row)}>Edit</button>
                          )}
                        </td>
                      </tr>

                      {row.Entries.slice(1).map((entry) =>
                        removedEntryIds.includes(entry._id) ? null : (
                          <tr key={entry._id} className="managereturn-sub-row">
                            <td colSpan="2" />
                            <td><span className="managereturn-sku">{entry.SKU || "—"}</span></td>
                            <td className="managereturn-title-cell" title={entry.Title || ""}>{entry.Title || "—"}</td>
                            <td>{entry.Edition || "—"}</td>
                            <td>{entry.Medium || "—"}</td>
                            <td>
                              {editId === row._id ? (
                                <input
                                  className="managereturn-quantity-input"
                                  type="number"
                                  value={editedQuantities[entry._id] || 0}
                                  onChange={(e) =>
                                    setEditedQuantities((prev) => ({
                                      ...prev,
                                      [entry._id]: e.target.value,
                                    }))
                                  }
                                  aria-label={"Return quantity for " + entry.SKU}
                                />
                              ) : (
                                entry.Return_Quantity
                              )}
                            </td>
                            <td>{entry.Good_Quantity}</td>
                            <td>{entry.Bad_Quantity}</td>
                            <td colSpan="6">
                              {editId === row._id && (
                                <button
                                  className="managereturn-action-button managereturn-action-button--remove"
                                  type="button"
                                  onClick={() => setRemovedEntryIds((prev) => [...prev, entry._id])}
                                >
                                  Remove item
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      )}

                      {editId === row._id &&
                        newEntries.map((entry, idx) => (
                          <tr key={entry._id} className="managereturn-sub-row managereturn-new-row">
                            <td colSpan="2" />
                            <td colSpan="5">
                              <select
                                className="managereturn-entry-select"
                                value={entry.SKU + "-" + entry.Title}
                                onChange={(e) => {
                                  const selected = listData.find(
                                    (item) => item.sku + "-" + item.title === e.target.value
                                  );
                                  if (selected) {
                                    updateNewEntry(idx, "SKU", selected.sku);
                                    updateNewEntry(idx, "Title", selected.title);
                                    updateNewEntry(idx, "Edition", selected.edition);
                                    updateNewEntry(idx, "Medium", selected.medium);
                                    updateNewEntry(idx, "mrp", selected.mrp);
                                    updateNewEntry(idx, "Return_Quantity", 1);
                                    updateNewEntry(idx, "Good_Quantity", 1);
                                    setEditedQuantities((prev) => ({
                                      ...prev,
                                      [entry._id]: 1,
                                    }));
                                  }
                                }}
                              >
                                <option value="">-- Select SKU - Title --</option>
                                {listData.map((item) => (
                                  <option key={item.sku} value={item.sku + "-" + item.title}>
                                    {item.sku} - {item.title}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                className="managereturn-quantity-input"
                                type="number"
                                placeholder="Return Qty"
                                value={entry.Return_Quantity}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  updateNewEntry(idx, "Return_Quantity", value);
                                  updateNewEntry(idx, "Good_Quantity", value);
                                  setEditedQuantities((prev) => ({
                                    ...prev,
                                    [entry._id]: value,
                                  }));
                                }}
                                aria-label={"Return quantity for new item " + (idx + 1)}
                              />
                            </td>
                            <td>
                              <input
                                className="managereturn-quantity-input"
                                type="number"
                                placeholder="Good Qty"
                                value={entry.Good_Quantity}
                                onChange={(e) => {
                                  const updated = [...newEntries];
                                  updated[idx].Good_Quantity = Number(e.target.value);
                                  setNewEntries(updated);
                                }}
                                aria-label={"Good quantity for new item " + (idx + 1)}
                              />
                            </td>
                            <td colSpan="6">
                              <button
                                className="managereturn-action-button managereturn-action-button--remove"
                                type="button"
                                onClick={() => setNewEntries((prev) => prev.filter((_, i) => i !== idx))}
                              >
                                Remove row
                              </button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="managereturn-empty-state">
              <span className="managereturn-empty-icon" aria-hidden="true">↩</span>
              <strong>No return records found</strong>
              <p>Return GRN records will appear here when they are available.</p>
            </div>
          )}
        </section>

        <div className="managereturn-footer-note">
          <span aria-hidden="true">i</span>
          Return records are sorted by date, newest first.
        </div>
      </div>
    </main>
  );

}
export default Managereturn;
